import {
  Row,
  Col,
  Button,
  message,
  Table,
  Tag,
  Input,
  Modal,
  Descriptions,
  Alert,
} from "antd";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  approveVendorAmendment,
  cancelVendorAmendment,
  getVendorAmendmentById,
  rejectVendorAmendment,
  resetAmendmentStatus,
} from "store/vendor/vendorSlice";
import { routes } from "common/routes";
import { getPersistedUser } from "services/PersistService/PersistUserService";

const MESSAGE_KEY = "vendor_amendment_detail";

const statusColorMap: Record<string, string> = {
  PENDING: "gold",
  APPROVED: "green",
  REJECTED: "red",
  CANCELLED: "default",
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const FileLink = ({ url }: { url: unknown }) =>
  url ? (
    <a href={String(url)} target="_blank" rel="noreferrer">
      View file
    </a>
  ) : (
    <>-</>
  );

const VendorAmendmentDetailPage = () => {
  useDocumentTitle("Vendor Request Detail");
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentAmendment, amendmentStatus, amendmentError } = useAppSelector(
    (state) => state.vendors
  );
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelRemarks, setCancelRemarks] = useState("");
  // Guards against a leftover "success" status from a submit on another page
  const [actionTaken, setActionTaken] = useState(false);

  const currentUserId = Number(getPersistedUser()?.user?.id);
  const isMaker = currentAmendment?.requestedBy === currentUserId;
  const isPending = currentAmendment?.status === "PENDING";
  const isToggleRequest = currentAmendment?.requestType === "TOGGLE_STATUS";

  useEffect(() => {
    dispatch(resetAmendmentStatus());
    if (id) {
      dispatch(getVendorAmendmentById(Number(id)));
    }
    return () => {
      dispatch(resetAmendmentStatus());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (amendmentStatus === "loading") {
      message.loading({ content: "Loading", key: MESSAGE_KEY });
    } else if (amendmentStatus === "saving") {
      message.loading({ content: "Processing", key: MESSAGE_KEY });
    } else if (amendmentStatus === "success" && actionTaken) {
      message.success({ content: "Request updated", key: MESSAGE_KEY });
      navigate(`/${routes.VENDOR.AMENDMENTS}`);
    } else if (amendmentStatus === "error" && amendmentError) {
      message.error({ content: amendmentError, key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
  }, [amendmentStatus, amendmentError, navigate, actionTaken]);

  const handleApprove = () => {
    if (!currentAmendment) return;
    setActionTaken(true);
    dispatch(approveVendorAmendment({ id: currentAmendment.id }));
  };

  const handleReject = () => {
    if (!currentAmendment || !rejectRemarks.trim()) {
      message.error("Remarks are required to reject");
      return;
    }
    setActionTaken(true);
    dispatch(rejectVendorAmendment({ id: currentAmendment.id, remarks: rejectRemarks.trim() }));
    setRejectModalOpen(false);
  };

  const handleCancel = () => {
    if (!currentAmendment) return;
    setActionTaken(true);
    dispatch(cancelVendorAmendment({
      id: currentAmendment.id,
      remarks: cancelRemarks.trim() || undefined,
    }));
    setCancelModalOpen(false);
  };

  const payload = currentAmendment?.requestedPayload ?? {};
  const address = (payload.address ?? {}) as Record<string, unknown>;
  const metadata = (payload.metadata ?? {}) as Record<string, unknown>;
  const bank = (metadata.bankAccountDetails ?? {}) as Record<string, unknown>;
  const purchaseCodes = Array.isArray(metadata.purchaseCodes)
    ? (metadata.purchaseCodes as Record<string, unknown>[])
    : [];

  const changeColumns = [
    { title: "Field", dataIndex: "field", key: "field" },
    {
      title: "Old Value",
      dataIndex: "oldValue",
      key: "oldValue",
      render: (value: unknown) => formatValue(value),
    },
    {
      title: "New Value",
      dataIndex: "newValue",
      key: "newValue",
      render: (value: unknown) => formatValue(value),
    },
  ];

  const purchaseCodeColumns = [
    {
      title: "Purchase SKU Code",
      dataIndex: "purchaseSkuCode",
      key: "purchaseSkuCode",
      render: (value: unknown) => formatValue(value),
    },
    {
      title: "Reliability Score",
      key: "reliabilityScore",
      render: (_: unknown, record: Record<string, unknown>) =>
        formatValue(record.reliabilityScore ?? record.relibilityScore),
    },
  ];

  const changes = currentAmendment?.changes ?? [];

  return (
    <>
      <Row>
        <Col xs={24}>
          <AppPageHeader title={`Vendor Request #${id}`} />
        </Col>
      </Row>

      {currentAmendment && (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: "1.5rem" }}>
            <Descriptions.Item label="Request ID">{currentAmendment.id}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColorMap[currentAmendment.status] ?? "default"}>
                {currentAmendment.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Request Type">{currentAmendment.requestType}</Descriptions.Item>
            <Descriptions.Item label="Vendor ID">{currentAmendment.entityId ?? "-"}</Descriptions.Item>
            <Descriptions.Item label="Requested By">{currentAmendment.requestedBy}</Descriptions.Item>
            <Descriptions.Item label="Requested At">
              {new Date(currentAmendment.requestedAt).toLocaleString()}
            </Descriptions.Item>
            {currentAmendment.metadata?.remarks && (
              <Descriptions.Item label="Review Remarks" span={2}>
                {currentAmendment.metadata.remarks}
              </Descriptions.Item>
            )}
          </Descriptions>

          {isPending && isMaker && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: "1rem" }}
              message="You submitted this request. You can cancel it while it is pending."
            />
          )}

          {isPending && !isMaker && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: "1rem" }}
              message="Review the requested changes below and approve or reject this request."
            />
          )}

          {changes.length > 0 && (
            <Table
              title={() => <strong>Requested Changes</strong>}
              columns={changeColumns}
              dataSource={changes.map((change, index) => ({
                ...change,
                key: `${change.field}-${index}`,
              }))}
              pagination={false}
              size="small"
              bordered
              style={{ marginBottom: "1.5rem" }}
            />
          )}

          {isToggleRequest ? (
            <Descriptions
              title="Requested Status Change"
              bordered
              size="small"
              column={1}
              style={{ marginBottom: "1.5rem", maxWidth: 480 }}
            >
              <Descriptions.Item label="New Status">
                <Tag color={payload.status === "ACTIVE" ? "green" : "red"}>
                  {formatValue(payload.status)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <>
              <Descriptions
                title="Requested Vendor Details"
                bordered
                size="small"
                column={2}
                style={{ marginBottom: "1.5rem" }}
              >
                <Descriptions.Item label="Name">{formatValue(payload.name)}</Descriptions.Item>
                <Descriptions.Item label="POC Name">{formatValue(payload.pocName)}</Descriptions.Item>
                <Descriptions.Item label="Primary Phone">{formatValue(payload.primaryPhone)}</Descriptions.Item>
                <Descriptions.Item label="Whatsapp Phone">{formatValue(payload.whatsappPhone)}</Descriptions.Item>
                <Descriptions.Item label="Email">
                  {formatValue(payload.vendorEmail ?? payload.email)}
                </Descriptions.Item>
                <Descriptions.Item label="PAN Number">{formatValue(payload.panNumber)}</Descriptions.Item>
                <Descriptions.Item label="Vendor Service">{formatValue(payload.vendorService)}</Descriptions.Item>
                <Descriptions.Item label="Vendor Type">{formatValue(payload.vendorType)}</Descriptions.Item>
                <Descriptions.Item label="Parent Vendor ID">
                  {formatValue(metadata.parentVendorId)}
                </Descriptions.Item>
                <Descriptions.Item label="Warehouse ID">{formatValue(payload.whId)}</Descriptions.Item>
                <Descriptions.Item label="Address" span={2}>
                  {[address.address1, address.address2, address.city, address.state, address.pincode]
                    .filter(Boolean)
                    .join(", ") || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="GSTIN">{formatValue(metadata.gstin)}</Descriptions.Item>
                <Descriptions.Item label="Market">{formatValue(metadata.market)}</Descriptions.Item>
                <Descriptions.Item label="Payment Method">{formatValue(metadata.paymentMethod)}</Descriptions.Item>
                <Descriptions.Item label="Latitude / Longitude">
                  {formatValue(metadata.latitude)} / {formatValue(metadata.longitude)}
                </Descriptions.Item>
                <Descriptions.Item label="Bank Account Name">{formatValue(bank.accountName)}</Descriptions.Item>
                <Descriptions.Item label="Bank Account Number">{formatValue(bank.accountNumber)}</Descriptions.Item>
                <Descriptions.Item label="Bank Name">{formatValue(bank.bankName)}</Descriptions.Item>
                <Descriptions.Item label="IFSC / Branch">
                  {formatValue(bank.ifscCode)} / {formatValue(bank.branchName)}
                </Descriptions.Item>
                <Descriptions.Item label="Cancelled Cheque">
                  <FileLink url={metadata.cancelledChequeFileUrl} />
                </Descriptions.Item>
                <Descriptions.Item label="PAN Image">
                  <FileLink url={metadata.panImageUrl} />
                </Descriptions.Item>
                <Descriptions.Item label="Aadhaar Image">
                  <FileLink url={metadata.adhaarImageUrl} />
                </Descriptions.Item>
                <Descriptions.Item label="GST Certificate">
                  <FileLink url={metadata.gstCertificateFileUrl} />
                </Descriptions.Item>
              </Descriptions>

              {purchaseCodes.length > 0 && (
                <Table
                  title={() => "Purchase Codes"}
                  columns={purchaseCodeColumns}
                  dataSource={purchaseCodes.map((code, index) => ({
                    ...code,
                    key: `code-${index}`,
                  }))}
                  pagination={false}
                  size="small"
                  bordered
                  style={{ marginBottom: "1.5rem", maxWidth: 480 }}
                />
              )}
            </>
          )}

          <Row gutter={12}>
            {isPending && !isMaker && (
              <>
                <Col>
                  <Button type="primary" onClick={handleApprove} loading={amendmentStatus === "saving"}>
                    Approve
                  </Button>
                </Col>
                <Col>
                  <Button danger onClick={() => setRejectModalOpen(true)} loading={amendmentStatus === "saving"}>
                    Reject
                  </Button>
                </Col>
              </>
            )}
            {isPending && isMaker && (
              <Col>
                <Button danger onClick={() => setCancelModalOpen(true)} loading={amendmentStatus === "saving"}>
                  Cancel Request
                </Button>
              </Col>
            )}
            <Col>
              <Button onClick={() => navigate(`/${routes.VENDOR.AMENDMENTS}`)}>
                Back to Requests
              </Button>
            </Col>
          </Row>
        </>
      )}

      <Modal
        title="Reject Request"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={handleReject}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              danger
              onClick={handleReject}
              loading={amendmentStatus === "saving"}
              disabled={!rejectRemarks.trim()}
            >
              Reject
            </Button>
          </div>
        }
      >
        <p>Remarks are required to reject this request.</p>
        <Input.TextArea
          rows={3}
          value={rejectRemarks}
          onChange={(e) => setRejectRemarks(e.target.value)}
          placeholder="Enter rejection remarks"
        />
      </Modal>

      <Modal
        title="Cancel Request"
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        onOk={handleCancel}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setCancelModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              danger
              onClick={handleCancel}
              loading={amendmentStatus === "saving"}
            >
              Cancel Request
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to cancel this request?</p>
        <Input.TextArea
          rows={3}
          value={cancelRemarks}
          onChange={(e) => setCancelRemarks(e.target.value)}
          placeholder="Optional remarks"
        />
      </Modal>
    </>
  );
};

export default VendorAmendmentDetailPage;
